import React, {useCallback, useEffect, useMemo, useState} from 'react';
import Calendar from 'react-calendar';
import {useRecoilState} from 'recoil';
import {loadingAtom} from '../../atoms';
import {getCompanyClosedConfiguration, saveCompanyClosedConfiguration} from '../../utils/requestUtils';
import '../../css/react-calendar-shared.css';
import './CompanyClosedDaysCalendar.css';
import CalendarLegend from '../common/CalendarLegend';

const WEEKDAY_LABELS = [
    {value: 0, label: 'Dom'},
    {value: 1, label: 'Seg'},
    {value: 2, label: 'Ter'},
    {value: 3, label: 'Qua'},
    {value: 4, label: 'Qui'},
    {value: 5, label: 'Sex'},
    {value: 6, label: 'Sáb'},
];

const formatDateKey = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const CompanyClosedDaysCalendar = ({disabled = false}) => {
    const [, setLoading] = useRecoilState(loadingAtom);
    const [selectedDates, setSelectedDates] = useState(new Set());
    const [selectedWeekdays, setSelectedWeekdays] = useState(new Set());
    const [activeStartDate, setActiveStartDate] = useState(new Date());
    const [errorMessage, setErrorMessage] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [isDirty, setIsDirty] = useState(false);

    const resetFeedback = () => {
        setErrorMessage('');
        setSuccessMessage('');
    };

    const loadConfiguration = useCallback(async () => {
        resetFeedback();
        setLoading(true);
        try {
            const response = await getCompanyClosedConfiguration();
            const datesSet = new Set((response?.dates || []));
            const weekdaysSet = new Set(response?.weekdays || []);
            setSelectedDates(datesSet);
            setSelectedWeekdays(weekdaysSet);
            setIsDirty(false);
        } catch (error) {
            console.error('Erro ao carregar dias off:', error);
            setErrorMessage('Não foi possível carregar os dias off.');
        } finally {
            setLoading(false);
        }
    }, [setLoading]);

    useEffect(() => {
        loadConfiguration();
    }, [loadConfiguration]);

    const handleDayClick = (date) => {
        if (disabled) {
            return;
        }
        resetFeedback();
        const dateKey = formatDateKey(date);
        setSelectedDates((prev) => {
            const updated = new Set(prev);
            if (updated.has(dateKey)) {
                updated.delete(dateKey);
            } else {
                updated.add(dateKey);
            }
            return updated;
        });
        setIsDirty(true);
    };

    const handleWeekdayToggle = (weekday) => {
        if (disabled) {
            return;
        }
        resetFeedback();
        setSelectedWeekdays((prev) => {
            const updated = new Set(prev);
            if (updated.has(weekday)) {
                updated.delete(weekday);
            } else {
                updated.add(weekday);
            }
            return updated;
        });
        setIsDirty(true);
    };

    const tileClassName = useCallback(({date, view}) => {
        if (view !== 'month') {
            return null;
        }
        const dateKey = formatDateKey(date);
        const weekday = date.getDay();
        const isSpecificDate = selectedDates.has(dateKey);
        const isRecurring = selectedWeekdays.has(weekday);

        if (isSpecificDate && isRecurring) {
            return 'company-closed-day-both';
        }
        if (isSpecificDate) {
            return 'company-closed-day-specific';
        }
        if (isRecurring) {
            return 'company-closed-day-recurring';
        }

        if (isRecurring) {
            return 'company-closed-day-recurring';
        }
        return 'company-open-day';
    }, [selectedDates, selectedWeekdays]);

    const onSave = async () => {
        if (disabled || !isDirty) {
            return;
        }
        resetFeedback();
        setLoading(true);
        try {
            const payload = {
                dates: Array.from(selectedDates).sort(),
                weekdays: Array.from(selectedWeekdays).sort((a, b) => a - b)
            };
            await saveCompanyClosedConfiguration(payload);
            setSuccessMessage('Dias off salvos com sucesso.');
            setIsDirty(false);
        } catch (error) {
            console.error('Erro ao salvar dias off:', error);
            setErrorMessage('Não foi possível salvar os dias. Tente novamente.');
        } finally {
            setLoading(false);
        }
    };

    const closedDaysLegendItems = [
        {key: 'open', swatchClass: 'company-open-day', label: 'Dia aberto'},
        {key: 'closed', swatchClass: 'company-closed-day-specific', label: 'Dia off'},
    ];

    return (
        <div className="company-closed-days-calendar">
            <div className="header-label">Dias Off</div>
            <div className="weekday-selector">
                <div className="weekday-selector-buttons">
                    {WEEKDAY_LABELS.map(({value, label}) => (
                        <button
                            key={value}
                            type="button"
                            className={selectedWeekdays.has(value) ? 'weekday-button selected' : 'weekday-button'}
                            onClick={() => handleWeekdayToggle(value)}
                            disabled={disabled}
                        >
                            {label}
                        </button>
                    ))}
                </div>
            </div>
            <div className="calendar-wrapper">
                <Calendar
                    locale="pt-BR"
                    onClickDay={handleDayClick}
                    activeStartDate={activeStartDate}
                    onActiveStartDateChange={({activeStartDate}) => setActiveStartDate(activeStartDate)}
                    tileClassName={tileClassName}
                    className={disabled ? 'calendar-disabled' : ''}
                />
                <CalendarLegend items={closedDaysLegendItems} />
            </div>
            <div className="calendar-actions">
                <button
                    onClick={loadConfiguration}
                    disabled={disabled}
                >
                    Recarregar
                </button>
                <button
                    onClick={onSave}
                    disabled={disabled || !isDirty}
                >
                    Confirmar
                </button>
            </div>
            {successMessage && (
                <div className="calendar-status calendar-status-success">
                    {successMessage}
                </div>
            )}
            {errorMessage && (
                <div className="calendar-status calendar-status-error">
                    {errorMessage}
                </div>
            )}
        </div>
    );
};

export default CompanyClosedDaysCalendar;


